import { ErrorIcon } from '@/components/icons'
import CalenderIcon from '@/components/icons/Calender'
import HardwareTimerIcon from '@/components/icons/HardwareTimer'
import {
  FormHelperText,
  IconButton,
  InputAdornment,
  Stack,
  SxProps,
  TextField,
  Typography,
  formHelperTextClasses,
} from '@mui/material'
import {
  DesktopDateTimePicker,
  LocalizationProvider,
} from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useState } from 'react'
import { Controller, UseFormReturn, useController } from 'react-hook-form'
import classes from './styles.module.scss'

interface DateTimeControlProps {
  name: string
  form: UseFormReturn<any, any>

  width?: string
  height?: number
  sx?: SxProps
  className?: any
  label?: string
  isRequired?: boolean
  placeholder?: any
  style?: {
    height?: number
    borderRadius?: string
    width?: {
      xs?: string | number
    }
    marginTop?: string
    padding?: string
    background?: string
    opacity?: string | number
  }
  startIcon?: boolean
  labelStart?: string
  minDate?: Date
  colorWtLabel?: boolean
  maxDate?: Date
  format?: string
  ampm?: boolean
  views?: Array<'day' | 'hours' | 'minutes' | 'month' | 'seconds' | 'year'>
}

const DateTimeControl = ({
  name,
  form,
  width,
  label,
  labelStart,
  colorWtLabel,
  sx,
  style,
  isRequired,
  placeholder,
  startIcon = false,
  minDate,
  maxDate,
  format = 'dd-MM-yyyy HH:mm:ss',
  ampm,
  views = ['year', 'month', 'day', 'hours', 'minutes', 'seconds'],
  ...props
}: DateTimeControlProps) => {
  const { control } = form || { control: null }

  const {
    formState: { errors },
  } = control
    ? // eslint-disable-next-line react-hooks/rules-of-hooks
      useController({ name, control })
    : {
        formState: { errors: undefined },
      }

  const [openDate, setOpenDate] = useState(false)

  const isTimeOnlyMode =
    views.length > 0 &&
    views.every((view) => ['hours', 'minutes', 'seconds'].includes(view))
  const hasTimeViews = views.some((view) =>
    ['hours', 'minutes', 'seconds'].includes(view),
  )

  const is12HourFormat = /(^|[^H])h{1,2}([^H]|$)/.test(format)
  const resolvedAmpm = ampm ?? (isTimeOnlyMode ? false : is12HourFormat)
  const PickerIcon = isTimeOnlyMode ? HardwareTimerIcon : CalenderIcon

  return (
    <Stack spacing="12px" width={width}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Controller
          name={name}
          control={control}
          render={({ field }) => {
            return (
              <DesktopDateTimePicker
                inputFormat={format}
                views={views}
                ampm={resolvedAmpm}
                open={openDate}
                PopperProps={{
                  sx: {
                    '& .PrivatePickersYear-root': {
                      '& .Mui-selected': {
                        backgroundColor: '#004744',
                        color: '#fff',
                      },
                    },
                    '& .PrivatePickersMonth-root': {
                      color: 'text.main',
                      '&.Mui-selected': {
                        backgroundColor: '#004744',
                        color: '#fff',
                      },
                    },
                    ...(hasTimeViews && {
                      '& .MuiPickersLayout-contentWrapper': {
                        minWidth: '320px',
                        display: 'flex',
                        justifyContent: 'center',
                      },
                      '& .MuiClockPicker-root': {
                        margin: '0 auto',
                      },
                      '& .MuiTimeClock-root': {
                        margin: '8px auto',
                      },
                      '& .MuiClock-root': {
                        backgroundColor: '#eef3f2',
                        margin: '0 auto',
                      },
                      '& .MuiClockNumber-root': {
                        color: '#00ad4f !important',
                        fontWeight: 600,
                        width: '32px',
                        height: '32px',
                        lineHeight: '32px',
                        fontSize: '0.875rem',
                      },
                      '& .MuiClockNumber-root.MuiClockNumber-clockNumberInner': {
                        color: '#111111 !important',
                        opacity: 1,
                        fontWeight: 700,
                      },
                      '& .MuiClockNumber-root.Mui-selected': {
                        color: '#fff !important',
                      },
                      '& .MuiClockPointer-root': {
                        backgroundColor: '#004744',
                      },
                      '& .MuiClockPointer-thumb': {
                        borderColor: '#004744',
                        backgroundColor: '#004744',
                      },
                      '& .MuiMultiSectionDigitalClockSection-item': {
                        color: '#004744',
                      },
                      '& .MuiMultiSectionDigitalClockSection-item.Mui-selected':
                        {
                          color: '#fff',
                          backgroundColor: '#004744',
                        },
                    }),
                  },
                }}
                PaperProps={{
                  sx: {
                    backgroundColor: 'background.overrideDefault',
                    mt: '10px',
                    color: 'text.main',
                    '& .MuiPickersArrowSwitcher-button': {
                      color: 'text.main',
                      '&.Mui-disabled': {
                        color: 'text.main',
                        opacity: '0.4',
                      },
                    },
                    '& .MuiPickersCalendarHeader-switchViewIcon': {
                      color: 'text.main',
                    },
                    '& .MuiDayPicker-weekDayLabel': {
                      color: 'text.main',
                    },
                    '& .MuiPickersDay-root': {
                      backgroundColor: 'transparent',
                      color: 'text.main',
                      '&.Mui-selected': {
                        backgroundColor: '#004744',
                        color: '#fff',
                      },
                      '&.Mui-disabled': {
                        color: 'text.main',
                        opacity: '0.4',
                      },
                    },
                    ...(hasTimeViews && {
                      '& .MuiPickersToolbar-root': {
                        color: '#004744',
                      },
                      '& .MuiPickersToolbarText-root': {
                        color: '#004744',
                      },
                      '& .MuiPickersToolbarText-root.Mui-selected': {
                        color: '#004744',
                        fontWeight: 700,
                      },
                      '& .MuiPickersLayout-actionBar .MuiButton-root': {
                        color: '#004744',
                        fontWeight: 700,
                      },
                      '& .MuiTimeClock-root': {
                        marginTop: 0,
                        marginBottom: 0,
                      },
                    }),
                  },
                }}
                onOpen={() => setOpenDate(true)}
                minDateTime={minDate}
                maxDateTime={
                  maxDate ? maxDate : new Date('02-07-2156 13:28:15')
                }
                disableHighlightToday={true}
                onClose={() => setOpenDate(false)}
                renderInput={(params: any) => (
                  <Stack>
                    <TextField
                      {...params}
                      placeholder={placeholder}
                      label={label}
                      InputProps={
                        startIcon
                          ? {
                              startAdornment: (
                                <InputAdornment
                                  position="start"
                                  sx={{ mr: '5px !important' }}
                                >
                                  <IconButton>
                                    <PickerIcon sx={{ color: 'text.main' }} />
                                  </IconButton>
                                  {labelStart && (
                                    <Stack direction={'row'}>
                                      <Typography
                                        className={classes.labelStart}
                                        sx={{
                                          ...(colorWtLabel && {
                                            color: 'text.main',
                                          }),
                                        }}
                                      >
                                        {labelStart}
                                      </Typography>
                                    </Stack>
                                  )}
                                </InputAdornment>
                              ),
                              disableUnderline: true,
                            }
                          : {
                              endAdornment: (
                                <InputAdornment
                                  position="start"
                                  sx={{ ml: '5px !important' }}
                                >
                                  <IconButton>
                                    <PickerIcon sx={{ color: 'text.main' }} />
                                  </IconButton>
                                </InputAdornment>
                              ),
                              disableUnderline: true,
                            }
                      }
                      inputProps={{
                        ...params.inputProps,
                        readOnly: true,
                      }}
                      onClick={() => setOpenDate(true)}
                      sx={{
                        gap: '2px',
                        lineHeight: '115%',
                        borderColor: !!errors?.[name]
                          ? 'error.main'
                          : 'background.overrideDefault',
                        backgroundColor: !!errors?.[name]
                          ? 'error.main'
                          : 'background.overrideDefault',
                        '&:hover': {
                          borderColor: !!errors?.[name]
                            ? 'error.main'
                            : '#004F4B',
                          backgroundColor: !!errors?.[name]
                            ? 'error.main'
                            : 'background.overrideDefault',
                        },
                        [`& .MuiInputLabel-root`]: {
                          lineHeight: '16px',
                          fontWeight: '700',
                          fontSize: '12px',
                          left: '4px',
                          padding: '8px 16px',
                          color: 'text.main',
                        },
                        [`& .Mui-disabled`]: {
                          backgroundColor: !!errors?.[name]
                            ? 'error.main'
                            : 'background.overrideDefault',
                          borderRadius: 'inherit',
                        },

                        ...(isRequired && {
                          [`& .MuiInputLabel-root::after`]: {
                            content: '"*"',
                            color: '#ED2121',
                            fontFamily: 'inherit',
                            verticalAlign: 'middle',
                            fontSize: '14px',
                          },
                        }),

                        [`& .MuiInputBase-root`]: {
                          color: 'text.main',
                          borderBottomLeftRadius: style?.borderRadius,
                          borderBottomRightRadius: style?.borderRadius,
                          borderTopRightRadius: style?.borderRadius,
                          borderTopLeftRadius: style?.borderRadius,
                          backgroundColor: !!errors?.[name]
                            ? 'error.main'
                            : 'background.overrideDefault',
                        },
                        [`& .MuiInputBase-input`]: {
                          borderBottomLeftRadius: style?.borderRadius,
                          borderBottomRightRadius: style?.borderRadius,
                          ...(isTimeOnlyMode && {
                            fontVariantNumeric: 'tabular-nums',
                            letterSpacing: '0.03em',
                          }),
                        },
                        [`& .MuiFilledInput-input`]: {
                          maxHeight: '17px',
                          paddingLeft: '16px',
                        },

                        borderRadius: '8px',
                        width: style?.width?.xs,
                        ...style,
                        ...sx,
                      }}
                      {...props}
                      variant="standard"
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                    {errors?.[name]?.message && (
                      <Stack
                        direction="row"
                        spacing="5px"
                        mt="3px"
                        color="#FF3030"
                      >
                        <ErrorIcon />
                        <FormHelperText
                          id="component-helper-text"
                          error
                          sx={{
                            width: '1',
                            fontSize: '12px',
                            fontWeight: '400',
                            lineHeight: '115%',
                            flex: 'none',
                            letterSpacing: '0.045em',
                            [`&.${formHelperTextClasses.error}`]: {
                              color: 'error.main',
                              marginLeft: '9px',
                              marginTop: '2px',
                            },
                          }}
                        >
                          {errors?.[name]?.message as string}
                        </FormHelperText>
                      </Stack>
                    )}
                  </Stack>
                )}
                {...field}
              />
            )
          }}
        />
      </LocalizationProvider>
    </Stack>
  )
}

export default DateTimeControl
